<template>
  <super-admin-layout>
    <v-container fluid class="invoices-page">
      <v-row>
        <v-col cols="12" md="3">
          <v-card flat>
            <v-card-actions>
              <v-text-field placeholder="Search by company, contact or invoice" prepend-icon="fa-search" v-model="search"
                type="text" clearable clear-icon="fa-times" @click:clear="resetSearch" @keyup="applySearch"
                class="search-box"></v-text-field>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card flat class="v-card-bg-none">
            <v-card-actions>
              <v-select :items="statusOptions" :clearable="true" placeholder="Status" item-value="id" item-text="name"
                v-on:change="filterByStatus($event)" class="search-box"></v-select>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center">
          <p class="invoices-intro mb-0">
            Buy IMPROVE purchases paid by invoice, unpaid first. Stripe issues the invoice with 30 day terms and
            creates the account when it sees the payment. If the money arrived in our own bank instead, record it
            here and the account is created the same way.
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-skeleton-loader v-if="loading && isTabletOrDesktop" type="table" :types="{ 'table-heading': '' }"></v-skeleton-loader>
          <v-skeleton-loader v-if="loading && !isTabletOrDesktop" type="table" :types="{ 'table-heading': '', 'table-thead' : 'heading@1' ,'table-row':'text@1'}" class="slider-table"></v-skeleton-loader>
          <v-data-table :headers="headers" :options.sync="options" :server-items-length="serverItemsLength"
            v-show="!loading" :items="invoices" item-key="uuid" :item-class="rowClass"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }">

            <template v-slot:item.company_name="{ item }">
              <router-link v-if="item.company_id" :to="'/super-admin/company/' + item.company_id" class="company-link">{{ item.company_name }}</router-link>
              <span v-else class="font-weight-bold">{{ item.company_name }}</span>
              <div class="grey--text caption">{{ item.contact_name }} &lt;{{ item.contact_email }}&gt;</div>
            </template>

            <template v-slot:item.amount_gross_pence="{ item }">
              <span v-if="item.amount_gross_pence !== null">{{ pounds(item.amount_gross_pence) }}</span>
              <div v-if="item.amount_net_pence !== null" class="grey--text caption">{{ pounds(item.amount_net_pence) }} + VAT</div>
            </template>

            <template v-slot:item.invoice_sent_at="{ item }">{{ day(item.invoice_sent_at) }}</template>

            <template v-slot:item.invoice_due_at="{ item }">
              <span>{{ day(item.invoice_due_at) }}</span>
              <div v-if="item.status == 'invoice_sent'" :class="item.overdue ? 'error--text' : (item.days_to_due <= 7 ? 'orange--text text--darken-2' : 'grey--text')" class="caption">
                {{ item.overdue ? 'Overdue' : (item.days_to_due + ' days left') }}
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item)" text-color="white">{{ statusLabel(item) }}</v-chip>
              <div v-if="item.invoice_paid_at" class="caption mt-1">
                Paid {{ day(item.invoice_paid_at) }}<span v-if="item.marked_paid_by"><br />recorded by {{ item.marked_paid_by }}</span>
              </div>
            </template>

            <template v-slot:item.uuid="{ item }">
              <a v-if="item.invoice_url" :href="item.invoice_url" target="_blank" rel="noopener" class="mr-2">
                <v-btn outlined small color="secondary">Invoice</v-btn>
              </a>
              <v-tooltip bottom v-if="item.status == 'invoice_sent'">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" outlined small color="secondary" @click.prevent="showPaidDialog(item)">Mark paid</v-btn>
                </template>
                <span>The payment arrived outside Stripe (our own bank). Records it and creates the account.</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="paidDialog" max-width="560px" scrollable persistent>
      <v-card>
        <v-card-title :class="this.$gf.headerColor()" style="color: white;">
          <span>Record a payment received outside Stripe</span>
        </v-card-title>
        <v-container class="dialog-container">
          <v-row>
            <v-col cols="12">
              <p>
                <strong>{{ paidItem.company_name }}</strong>, invoice for
                {{ paidItem.amount_gross_pence !== null ? pounds(paidItem.amount_gross_pence) : 'the agreed amount' }}
                including VAT, due {{ day(paidItem.invoice_due_at) }}.
              </p>
              <p class="error--text">
                Only do this when the money is actually in our bank. Stripe records the invoice as paid without
                taking a payment, and the customer's account is created within a minute, with their welcome email
                and signed agreement.
              </p>
              <v-textarea v-model="paidNote" label="How and when it was paid (for the audit trail)" rows="3"
                :error-messages="messages.note" filled></v-textarea>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions class="btn-section pa-5">
          <v-spacer></v-spacer>
          <template v-if="paidLoading">
            <v-btn color="primary" loading></v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closePaidDialog">Cancel</v-btn>
            <v-btn color="primary" :disabled="!paidNote" @click="processPaid">Record payment</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </super-admin-layout>
</template>

<script>
import SuperAdminLayout from '@/layouts/SuperAdmin';
import invoiceAPI from '@/api/invoice.js';

export default {
  name: 'SuperAdminInvoices',
  components: {
    SuperAdminLayout,
  },
  data() {
    return {
      headers: [
        { text: 'Company', value: 'company_name', sortable: false },
        { text: 'Plan', value: 'plan_slug', sortable: false },
        { text: 'Amount', value: 'amount_gross_pence', sortable: false },
        { text: 'Sent', value: 'invoice_sent_at', sortable: false },
        { text: 'Due', value: 'invoice_due_at', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: '', value: 'uuid', sortable: false, align: 'end' },
      ],
      invoices: [],
      search: '',
      searchParams: '',
      selectedStatus: '',
      statusOptions: [
        { id: 'invoice_sent', name: 'Awaiting payment' },
        { id: 'provisioned', name: 'Paid' },
      ],
      pagination: {},
      options: { page: 1, itemsPerPage: 25 },
      loading: false,
      timer: null,
      paidDialog: false,
      paidItem: {},
      paidNote: '',
      paidLoading: false,
      messages: {},
      errorMessage: 'Oops, something went wrong! Please check information is correct.',
    };
  },
  computed: {
    serverItemsLength: function () {
      if (this.pagination && this.pagination.total) {
        return this.pagination.total;
      }
      return this.invoices.length;
    },
    params: function () {
      const o = this.options;
      const limit = o.itemsPerPage === -1 ? 100 : o.itemsPerPage;
      return '?page=' + (o.page || 1) + '&limit=' + limit;
    },
    isTabletOrDesktop() {
      return this.$vuetify.breakpoint.width >= 820;
    },
  },
  watch: {
    options: {
      handler() {
        this.getInvoices();
      },
      deep: true,
    },
  },
  methods: {
    getInvoices() {
      this.loading = true;
      invoiceAPI
        .getInvoices(this.params, this.searchParams, this.selectedStatus)
        .then((resp) => {
          this.invoices = resp.data.data;
          this.pagination = resp.data.meta.pagination;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(this.errorMessage);
        });
    },
    applySearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.options.page = 1;
        this.searchParams = this.search ? '&search=' + encodeURIComponent(this.search) : '';
        this.getInvoices();
      }, 700);
    },
    resetSearch() {
      this.options.page = 1;
      this.search = '';
      this.applySearch();
    },
    filterByStatus(status) {
      this.selectedStatus = status ? '&status=' + status : '';
      this.options.page = 1;
      this.getInvoices();
    },
    day(value) {
      if (!value) return '';
      return String(value).slice(0, 10);
    },
    pounds(pence) {
      return '£' + (pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    statusColor(item) {
      if (item.status == 'provisioned') return 'success';
      if (item.status == 'invoice_sent') return item.overdue ? 'error' : 'orange darken-2';
      return 'grey';
    },
    statusLabel(item) {
      return { invoice_sent: 'Awaiting payment', provisioned: 'Paid, account live', expired: 'Expired' }[item.status] || item.status;
    },
    rowClass(item) {
      return item.status == 'provisioned' ? 'invoice-paid' : '';
    },
    showPaidDialog(item) {
      this.paidItem = item;
      this.paidNote = '';
      this.messages = {};
      this.paidDialog = true;
    },
    closePaidDialog() {
      this.paidDialog = false;
      this.paidItem = {};
      this.paidNote = '';
      this.messages = {};
    },
    processPaid() {
      if (!this.paidNote) return;
      this.paidLoading = true;
      this.messages = {};
      invoiceAPI
        .markPaid(this.paidItem.uuid, this.paidNote)
        .then(() => {
          this.paidLoading = false;
          this.closePaidDialog();
          this.$snotify.success('Recorded. Stripe has the invoice as paid; the account is being created.');
          // The webhook lands within seconds; refresh once now and once after.
          this.getInvoices();
          setTimeout(() => this.getInvoices(), 8000);
        })
        .catch((err) => {
          this.paidLoading = false;
          this.messages = this.$gf.errorResponse(err) || {};
          const msg = err && err.response && err.response.data && err.response.data.message;
          this.$snotify.error(msg || this.errorMessage);
        });
    },
  },
};
</script>
<style>
.invoices-page .invoice-paid {
  opacity: 0.7;
}
.invoices-page .invoices-intro {
  font-size: 13px;
  color: #555;
}
.invoices-page .company-link {
  text-decoration: none;
  font-weight: 600;
}
</style>
